'use client';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useUser } from '@clerk/nextjs';
import { useMutation, useQuery } from 'convex/react';
import { Star } from 'lucide-react';
import { api } from '../../convex/_generated/api';
import { usePostHog } from 'posthog-js/react';

interface BookmarkButtonProps {
  network: string;
  address: string;
  buttonVariant?:
    | 'ghost'
    | 'outline'
    | 'default'
    | 'secondary'
    | 'link'
    | 'destructive'
    | null
    | undefined;
  buttonSize?: 'sm' | 'lg' | 'icon' | 'default' | null | undefined;
  tooltipText?: string;
}

export function BookmarkAddressButton({
  network,
  address,
  buttonVariant = 'ghost',
  buttonSize = 'icon',
  tooltipText = 'Bookmark',
}: BookmarkButtonProps) {
  const { isSignedIn } = useUser();
  const posthog = usePostHog();

  const bookmarked = useQuery(api.addressBookmarks.check, {
    network,
    address,
  });
  const addBookmark = useMutation(api.addressBookmarks.add);
  const removeBookmark = useMutation(api.addressBookmarks.remove);

  const handleClick = () => {
    if (bookmarked) {
      removeBookmark({ network, address });
      posthog?.capture('address_bookmark_removed', {
        network,
        address,
      });
    } else {
      addBookmark({ network, address });
      posthog?.capture('address_bookmark_added', {
        network,
        address,
      });
    }
  };

  return isSignedIn ? (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant={buttonVariant}
            size={buttonSize}
            className="h-7 w-7"
            onClick={handleClick}
          >
            {bookmarked ? (
              <Star className="h-4 w-4 text-yellow-500" />
            ) : (
              <Star className="h-4 w-4" />
            )}
            <span className="sr-only">{tooltipText}</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{bookmarked ? 'Unbookmark' : 'Bookmark'}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : null;
}
